import React, { useCallback, useMemo, useEffect, useState, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSlate, ReactEditor } from 'slate-react';
import { Transforms, Range, Editor, Node, NodeEntry, Path } from 'slate';
import { getLastNode, move, getNode, getSelectionText } from '@udecode/slate-plugins'
import { toast } from 'components/Shared/Toast';
import { useForm } from 'react-hook-form';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ListGroup,
    ListGroupItem,
    Media,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Col,
    ButtonGroup
} from 'reactstrap'

import bootbox from 'components/Modals/Bootbox';
import { useEditorState } from '../../editor-context'
import { insertNodeAndScroll, isAlignBlock, getNodesByType } from '../../Canvas/TratoFormat'
import { getHeader, insertTable, insertClause, insertStatement } from '../../Canvas/TratoFormat/Elements'
import { useProfileState } from 'context/profile'

async function insertStatementOrClause(editor, { type, insertOne, text, t }) {
    const nodes = Array.from(getNodesByType(editor, type, { mode: 'highest' }))
    const allNodesOfType = nodes.flatMap(([n]) => {
        if (isAlignBlock(n.type)) {
            return n.children.filter(n2 => n2.type === type);
        }

        return n
    })
    
    let lastNodeOfType = allNodesOfType[allNodesOfType.length - 1];

    let number = 1;
    
    if (!nodes.length) {
        insertOne(editor, { block: 1, number, text })
    } else {
        let [lastElement] = getLastNode(editor);  
        if (isAlignBlock(lastElement.type)) {
            lastElement = lastElement.children[lastElement.children.length - 1];
        }   
        
        if (lastElement.type === type) {
            number = (lastElement.number || lastElement.index) + 1;
            const block = lastElement.block ?? 1;
            insertOne(editor, { block, number, text })
        } else {
            const continueNumeration = await bootbox.confirm({
                message: t(`editor.elements.ask_reset_${type}`),
                buttons: {
                    cancel: { label: t('editor.elements.reset') },
                    confirm: { label: t('editor.elements.continue') },
                },
            });

            let block = 1;
            if (continueNumeration) {
                number =(lastNodeOfType.number || lastNodeOfType.index) + 1;
                block = lastNodeOfType.block ?? 1;
            } else  {
                block = lastNodeOfType.block + 1
                number = 1
            }
            insertOne(editor, { block, number, text })
        }
    }
}


export const moveChildren = (
    editor,
    { at, to, match, start = 0 }
  ) => {
    let moved = 0;
    const parentPath = Path.isPath(at) ? at : at[1];
    const parentNode = Path.isPath(at) ? Node.get(editor, parentPath) : at[0];
  
    if (!Editor.isBlock(editor, parentNode)) return moved;
  
    for (let i = parentNode.children.length - 1; i >= start; i--) {
      const childPath = [...parentPath, i];
      const childNode = getNode(editor, childPath);
  
      if (!match || (childNode && match([childNode, childPath]))) {
        Transforms.moveNodes(editor, { at: childPath, to });
        moved++;
      }
    }
  
    return moved;
  };

export function ElementList() {
    const { t } = useTranslation()
    const {elementsMap, elements} = useElementList()
    const editor = useSlate()

    const handleCreateElementFromText = ({ type }) => {

        const validTypes = ['title', 'clause', 'statement', 'text'];

        // Checking if a text is selected or if a the type is valid
        if (!editor.selection || !validTypes.includes(type)) {
            return
        }
        
        // Getting the selected text
        const text = getSelectionText(editor);
        
        // Deleting the selected text from current node
        editor.deleteFragment()
        
        // Deselecting current selection so we can insert selected text at the end of the document
        Transforms.deselect(editor)
        
        // Adding a new element with the selected text
        const elementToAdd = elementsMap.get(type)
        elementToAdd.onAdd({ text })
    }

    return (
        <div>
            {elements.map(element => {
                return <ElementListItem
                            key={element.type}
                            type={element.type}
                            label={element.label}
                            description={element.description}
                            onAddElement={element.onAdd}
                            onCreateElementFromText={handleCreateElementFromText}
                        />
            })}
        </div>    
    )
}


function ElementListItem({ type, label, description, onAddElement, onCreateElementFromText }) { 
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const handleAddElement = () => {
        if (type === 'table') {
            setIsDialogOpen(true)
            return;
        }
        onAddElement({ type })
    }

    const toggleDialog = () => {
        setIsDialogOpen(isOpen => !isOpen)
    }
    const handleDialogSubmit = (data) => {
        onAddElement({ type, rows: data.rows, cols: data.cols })
        setIsDialogOpen(false)
    }

    const handleCreateElementFromText = (e) => {
        e.preventDefault();
        onCreateElementFromText({ type })
    }

    return (
        <ListGroup data-type={type} className="rounded-0">
            <ListGroupItem className="bg-light text-dark pointer p-0">
                <Media className="align-items-center">
                    <Media onClick={handleCreateElementFromText} className="p-3 border-right">
                        <img src={`/images/editor/icons/elements/${type}.svg`} alt={label} height="30px" />
                    </Media>
                    <Media body onClick={handleAddElement} className="p-2">
                        <Media className="font-weight-bold">{label}</Media>
                        <Media className=""><small className="">{description}</small></Media>
                    </Media>
                </Media>
            </ListGroupItem>
            <DialogModal isOpen={isDialogOpen} toggle={toggleDialog} onSubmit={handleDialogSubmit} />
        </ListGroup>
    )
}

export function useElementList()  {
    const { t } = useTranslation();
    const editor = useSlate()
    const { document: doc, currentElement } = useEditorState();
    const { profile } = useProfileState()

    const elementsMap = useMemo(() => new Map([
        ['header', { 
            type: 'header', 
            label: t('editor.elements.header_din_title'), 
            description: t('editor.elements.header_din_description'),
            onAdd: () => {
                insertNodeAndScroll(editor, { type: 'header', isNew: true, children: [{text: ''}] })
            }
        }],
        ['staticheader', { 
            type: 'staticheader', 
            label: t('editor.elements.header_title'), 
            description: t('editor.elements.header_description'),
            onAdd: () => {
                const headerNodes = getHeader({ doc })
                insertNodeAndScroll(editor, { type: 'static-header', isNew: true, children: headerNodes }) 
            }
        }],
        ['text', { 
            type: 'text', 
            label: t('editor.elements.text_title'), 
            description: t('editor.elements.text_description'),
            onAdd: ({ text = ''}) => {
                insertNodeAndScroll(editor, { isNew: true, type: 'text', children: [{ text }] })       
            }
        }],
        ['statement', { 
            type: 'statement', 
            label: t('editor.elements.statement_title'), 
            description: t('editor.elements.statement_description'),
            onAdd: ({ text = ''}) => {
                insertStatementOrClause(editor, {
                    type: 'statement',
                    insertOne: insertStatement,
                    text,
                    t
                })
            }
        }],
        ['clause', { 
            type: 'clause', 
            label: t('editor.elements.clause_title'), 
            description: t('editor.elements.clause_description'),
            onAdd: ({ text = ''}) => {
                insertStatementOrClause(editor, {
                    type: 'clause',
                    insertOne: insertClause,
                    text,
                    t
                })
            }
        }],
        ['title', { 
            type: 'title', 
            label: t('editor.elements.title_title'), 
            description: t('editor.elements.title_description'),
            onAdd: ({ text = ''}) => {
                insertNodeAndScroll(editor, { type: 'title',  isNew: true, children: [{ text }] })       
            }
        }],
        ['signature', { 
            type: 'signature', 
            label: t('editor.elements.signature_title'), 
            description: t('editor.elements.signature_description'),
            onAdd: () => {
                insertNodeAndScroll(editor, { type: 'signature',  isNew: true, children: [{ text: '' }], participants: doc.participants.filter(p => !p.isNotSigner).map(p => p._id) })       
            }
        }],
        ['break', { 
            type: 'break', 
            label: t('editor.elements.break_title'), 
            description: t('editor.elements.break_description'),
            onAdd: () => {
                insertNodeAndScroll(editor, { type: 'break', isNew: true, children: [{ text: '' }]})       
            }
        }],
        ['document_header', { 
            type: 'document_header', 
            label: t('editor.elements.document_header_title'), 
            description: t('editor.elements.document_header_description'),
            onAdd: () => {
                const nodes = Array.from(getNodesByType(editor, 'document_header'))
                if (nodes.length) return toast.error(t('editor.elements.document_header_error'));
                
                insertNodeAndScroll(editor, { type: 'document_header',  isNew: true, children: [{ text: '' }]})
            }
        }],
        ['document_footer', { 
            type: 'document_footer', 
            label: t('editor.elements.document_footer_title'),
            description: t('editor.elements.document_footer_description'),
            onAdd: () => {
                const nodes = Array.from(getNodesByType(editor, 'document_footer'))
                if (nodes.length) return toast.error(t('editor.elements.document_footer_error'));

                insertNodeAndScroll(editor, { type: 'document_footer',  isNew: true, children: [{ text: '' }]})
            }
        }],
        ['document_logo', { 
            type: 'document_logo', 
            label: t('editor.elements.document_logo_title'), 
            description: t('editor.elements.document_logo_description'),
            onAdd: () => {
                insertNodeAndScroll(editor, { type: 'text',  isNew: true, children: [{ text: '' }, { type: 'image', name: 'logo', width: 150, url: profile.office.logo, children: [{ text: '' }] },{ text: '' }] })
            }
        }],
        // { type: 'expiry', label: 'Vigencia', description: 'Duración del contrato.' }
        ['table', { 
            type: 'table', 
            label: t('editor.elements.table_title'), 
            description: t('editor.elements.table_description') ,
            onAdd: ({ rows = 5, cols = 5 }) => {
                insertTable(editor, Number(rows), Number(cols))
            }
        }],
        //{ type: 'payment_clause', label: 'Payment', description: 'Payment clause' },
    ]), [editor, t, currentElement, doc]);

    return { elementsMap, elements: Array.from(elementsMap.values()) }
}


function DialogModal({ isOpen, toggle, onSubmit }) {
    const { register, handleSubmit } = useForm()
    const { t } = useTranslation()

    if (!isOpen) {
        return null
    }

    return (
        <Modal centered isOpen={isOpen}> 
                    <ModalHeader>
                        {t('editor.table.title')}
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <Row tag={FormGroup}>
                                <Label className="control-label col-sm-7">{t('editor.table.columnsLabel')}:</Label>
                                <div className="col-sm-5">
                                    <Input 
                                        type="number" 
                                        className="form-control mb-2" 
                                        name="cols" 
                                        placeholder="Columnas" 
                                        max="7" 
                                        min="1"  
                                        defaultValue={5}
                                        innerRef={register}
                                    />
                                </div>
                            </Row>
                            <Row tag={FormGroup}>
                                <Label className="control-label col-sm-7" for="pwd">{t('editor.table.rowsLabel')}:</Label>
                                <div className="col-sm-5"> 
                                    <Input 
                                        type="number" 
                                        className="form-control mb-2" 
                                        name="rows" 
                                        placeholder="Filas" 
                                        max="50" 
                                        min="1" 
                                        defaultValue={5}
                                        innerRef={register}
                                    />
                                </div>
                            </Row>
                            {/* <FormGroup> 
                                <label className="control-label col-sm-7" for="pwd">Agregar Titulos: </label>
                                <div className="col-sm-5 text-center">
                                    <label className="switch">
                                        <input type="checkbox" name="headers" /><span className="slider round" />
                                    </label>
                                </div>
                            </div> */}
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonGroup className="d-flex flex-grow-1">
                            <button className="btn btn-sm btn-default" data-action="continue" onClick={toggle}>
                                {t('editor.action.close')}
                            </button>
                            <button className="btn btn-sm btn-primary" data-action="continue" onClick={handleSubmit(onSubmit)}>
                                {t('editor.action.create')}
                            </button>
                        </ButtonGroup>
                    </ModalFooter>
                </Modal>
    )
}